import { Globe, Monitor } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/common/EmptyState';
import { Spinner } from '@/components/ui/spinner';
import { useSecretAuditLogs } from '@/hooks/useAuditLogs';

function formatDate(ts: unknown): string {
  if (!ts) return '—';
  const date =
    typeof ts === 'object' && ts && 'toDate' in ts && typeof ts.toDate === 'function'
      ? (ts as { toDate: () => Date }).toDate()
      : new Date(String(ts));
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('ja-JP');
}

export function AuditLogsTab() {
  const { data, isLoading } = useSecretAuditLogs(100);

  if (isLoading) return <Spinner />;

  if (!data || data.length === 0) {
    return (
      <EmptyState
        icon={Monitor}
        title="まだ監査ログがありません"
        description="シークレット更新・管理者操作が発生するとここに記録されます"
      />
    );
  }

  return (
    <div className="max-w-4xl">
      <p className="text-xs text-[var(--text-secondary)] mb-4">
        直近 100 件のシークレット変更履歴を表示しています（owner のみ閲覧可能）
      </p>
      <div className="rounded bg-[var(--bg-surface)] border border-[var(--border)] overflow-hidden">
        {data.map((log) => (
          <div
            key={log.id}
            className="flex items-center gap-4 p-3 border-b border-[var(--border)] last:border-b-0 text-xs"
          >
            <Badge
              variant={
                log.action === 'delete' ? 'danger' : log.action === 'create' ? 'approved' : 'accent'
              }
            >
              {log.action}
            </Badge>
            <code className="text-[11px] font-mono bg-[var(--bg-hover)] px-2 py-0.5 rounded">
              {log.secretKey}
            </code>
            <div className="flex-1 min-w-0 text-[var(--text-secondary)] truncate">
              {log.performedByEmail}
            </div>
            <div className="flex items-center gap-1 text-[var(--text-muted)]">
              <Globe className="size-3" />
              <span className="font-mono">{log.ipAddress}</span>
            </div>
            <div className="text-[var(--text-muted)] font-mono text-[11px] whitespace-nowrap">
              {formatDate(log.timestamp)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
